"use client";

import React, { useRef, useState } from "react";
import IconArrowDown from "@lmn/lastminute-react-icons/icons-converted/lastminute-icons-solid/IconArrowDown.js";
import IconCross from "@lmn/lastminute-react-icons/icons-converted/lastminute-icons-solid/IconCross.js";
import {
  Container,
  Pill,
  ArrowWrapper,
  CloseButton,
  MenuContent,
  ButtonsContainer,
  CancelButton,
  ConfirmButton,
  StyledMenu,
} from "./ChipDropdown.styles";

interface ChipDropdownProps {
  label: string;
  isFilterActive?: boolean;
  onReset?: () => void;
  onConfirm?: () => void;
  onCancel?: () => void;
  children: React.ReactNode | ((closeMenu: () => void) => React.ReactNode);
  scrollable?: boolean;
  immediateApply?: boolean;
  hasPendingChanges?: boolean;
}

const ChipDropdown: React.FC<ChipDropdownProps> = ({
  label,
  isFilterActive = false,
  onReset,
  onConfirm,
  onCancel,
  children,
  scrollable = false,
  immediateApply = false,
  hasPendingChanges = true,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const buttonRef = useRef<HTMLButtonElement | null>(null);

  const handleOpen = () => {
    setIsOpen(true);
  };

  const handleClose = () => {
    onCancel?.();
    setIsOpen(false);
  };

  const closeMenu = () => {
    setIsOpen(false);
  };

  const handleConfirm = () => {
    onConfirm?.();
    setIsOpen(false);
  };

  const handleCancel = () => {
    onCancel?.();
    setIsOpen(false);
  };

  const handleReset = (e: React.MouseEvent | React.KeyboardEvent) => {
    e.stopPropagation();
    if (onReset) {
      onReset();
    }
  };

  const handleResetKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter" || e.key === " ") {
      e.preventDefault();
      handleReset(e);
    }
  };

  return (
    <Container>
      <Pill
        onClick={handleOpen}
        ref={buttonRef}
        $isOpen={isOpen}
        $isFilterActive={isFilterActive}
        type="button"
        aria-haspopup="true"
        aria-expanded={isOpen}
        data-testid={`chip-dropdown-${label.toLowerCase().replace(/\s+/g, "-").replace(/&/g, "")}`}
      >
        {label}
        {isFilterActive ? (
          <CloseButton
            onClick={handleReset}
            onKeyDown={handleResetKeyDown}
            aria-label={`${label} Filter zurücksetzen`}
            role="button"
            tabIndex={0}
            data-testid="chip-dropdown-reset"
          >
            <IconCross size={8} />
          </CloseButton>
        ) : (
          <ArrowWrapper aria-hidden="true">
            <IconArrowDown size={16} />
          </ArrowWrapper>
        )}
      </Pill>
      <StyledMenu
        isOpen={isOpen}
        onClose={handleClose}
        anchorEl={buttonRef.current}
        $disableInternalScroll={scrollable}
      >
        <MenuContent $scrollable={scrollable}>
          {typeof children === "function" ? children(closeMenu) : children}
        </MenuContent>
        {!immediateApply && (
          <ButtonsContainer>
            <CancelButton
              variant="secondary"
              appearance="ghost"
              onClick={handleCancel}
              disabled={!hasPendingChanges}
              data-testid="chip-dropdown-cancel"
            >
              Rückgängig
            </CancelButton>
            <ConfirmButton
              variant="primary"
              onClick={handleConfirm}
              data-testid="chip-dropdown-confirm"
            >
              Übernehmen
            </ConfirmButton>
          </ButtonsContainer>
        )}
      </StyledMenu>
    </Container>
  );
};

export default ChipDropdown;
